import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { OutageService } from '@common/service/outage.service';
import { UNKNOWN_DEPLOYMENT_REASON } from '@common/util/deployment-status.util';
import { OUTAGE_COPY } from '@common/util/outage.util';

/**
 * AIWB-1175 — the platform-outage screen (owner-approved design A, plus the dormant deploy variant C).
 * Everything it shows is derived from OutageService signals; it is mounted through SystemNotificationService.
 */
@Component({
  selector: 'app-outage-screen',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './outage-screen.component.html',
  styleUrls: ['./outage-screen.component.scss'],
})
export class OutageScreenComponent {
  protected readonly outage = inject(OutageService);
  protected readonly copy = OUTAGE_COPY;

  protected readonly upgrade = computed(() => this.outage.variant() === 'upgrade');
  protected readonly slow = computed(() => this.outage.stage() === 'slow');
  protected readonly exits = computed(() => this.outage.stage() !== 'initial');

  /** AIWB-1363 — an unknown reason would contradict the upgrading title, so that screen shows only deployment news. */
  protected readonly showReason = computed(
    () => this.outage.reasonEnabled && !(this.upgrade() && this.outage.reason() === UNKNOWN_DEPLOYMENT_REASON),
  );

  protected readonly title = computed(() => {
    if (this.upgrade()) {
      return OUTAGE_COPY.upgradeTitle;
    }
    return this.slow() ? OUTAGE_COPY.slowTitle : OUTAGE_COPY.outageTitle;
  });

  protected readonly body = computed(() => {
    if (this.upgrade()) {
      return this.slow() ? OUTAGE_COPY.upgradeSlowBody : OUTAGE_COPY.upgradeBody;
    }
    return this.slow() ? OUTAGE_COPY.slowBody : OUTAGE_COPY.outageBody;
  });

  protected readonly status = computed(() => {
    const feedback = this.outage.feedback();
    if (feedback === 'unreachable') {
      return OUTAGE_COPY.stillUnreachable;
    }
    if (feedback === 'back') {
      return OUTAGE_COPY.backOnline;
    }
    if (this.slow()) {
      return OUTAGE_COPY.slowStatus;
    }
    return this.upgrade() ? OUTAGE_COPY.upgradeStatus : OUTAGE_COPY.reconnecting;
  });

  protected readonly showDots = computed(() => !this.slow() && this.outage.feedback() === 'none');
}
